<template>
  <!-- TODO: use @plugins/modal/components/WaitModal.vue instead, dont duplicate it -->
  <content-modal
    class="my-drive-wait-modal async-load"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :use-state-button="true"
    :auto-hide-off="autoHideOff"
    :show-header="false"
    @modal-shown="actionShown"
    @modal-hidden="actionHidden"
    @modal-hide-request="actionHideRequest"
  >
    <template v-if="use_content" #content>
      <modal-content>
        <div
          class="graphic"
          :class="{
            _loading: isDuplicating,
          }"
        >
          <CatChasingBall v-if="isDuplicating" />
          <FallingBallSuccess v-if="isDuplicated" />
        </div>

        <h3 class="title">
          {{ setTitle }}
        </h3>
        <p class="message">{{ setContent }}</p>

        <!-- Upvote prompt: shown after copy/duplicate of MOE/CG lessons for non-admin users -->
        <prompt-tile
          v-if="showUpvote"
          :title="t('upvote_module_prompt_title')"
          :sub-title="t('upvote_module_prompt_subtitle')"
          :btn-icon-name="upvoteIconName"
          @action="toggleUpvote"
        />

        <div class="tools">
          <cv-button
            v-if="showPrimary"
            id="button_edit_lesson"
            kind="primary"
            :error="dataListButton.error"
            :loading="dataListButton.loading"
            :error-message="dataListButton.errorMessage"
            @click="actionPrimary"
            >{{ primaryButtonLabel }}
          </cv-button>
          <cv-button
            id="button_close"
            class="cancel"
            kind="tertiary"
            visible="true"
            @click="actionSecondary"
            >{{ secondaryButtonLabel }}
          </cv-button>
        </div>
      </modal-content>
    </template>
  </content-modal>
</template>

<script lang="ts" setup>
import modalEventBus from "@plugins/modal/service/event-bus";
import CatChasingBall from "@images/async-loading/cat-chasing-ball.svg?component";
import FallingBallSuccess from "@images/async-loading/falling-ball-success.svg?component";
import { ref, computed, onMounted, defineAsyncComponent } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { useVm } from "@utils/vm.ts";
import { useDirty } from "@plugins/common/composables/page-dirty";
import { useModuleUpvote } from "@modules/lesson/composables/useModuleUpvote";

const PromptTile = defineAsyncComponent(() => import("@/components/PromptTile.vue"));

const store = useStore();
const { vm } = useVm();
const { t } = useI18n();

// Data
const id = ref(0);
const closeAriaLabel = ref("Close");
const use_content = ref(true);
const size = ref("large");
const visible = ref(true);
const autoHideOff = ref(true);
const dataListButton = ref({
  kind: "primary",
  error: false,
  loading: false,
  errorMessage: t("api_action_generic_error_message"),
});

const { clearDirty } = useDirty(id.value);
const isStudent = computed(() => store.getters["auth/isStudent"]);
// Props
const props = defineProps({
  params: { type: Object, required: true },
});

// Computed
const primaryButtonLabel = computed(() => {
  if (isStudent.value) return t("go_to_my_drive");
  else return t("edit_lesson");
});

const secondaryButtonLabel = computed(() => {
  if (isStudent.value) return t("back_to_home");
  else return t("modal_close_aria_label");
});

const setTitle = computed(() => {
  if (isDuplicated.value) {
    return t("long_wait_time_duplicated_resource_title");
  }
  return t("long_wait_time_duplicating_resource_title");
});

const setContent = computed(() => {
  if (isDuplicated.value) {
    return t("long_wait_time_duplicated_resource_content");
  }
  return t("long_wait_time_duplicating_resource_content");
});

const status = computed(() => {
  return props.params.status;
});

const showPrimary = computed(() => {
  return isDuplicated.value;
});

const isDuplicating = computed(() => {
  return status.value === "duplicating";
});

const isDuplicated = computed(() => {
  return status.value === "duplicated";
});

// Upvote prompt — shown after successful copy/duplicate of MOE/CG lessons for non-admin users
const isAdmin = computed(() => Boolean(props.params.isAdmin));
const { upvoteIconName, toggleUpvote, fetchUpvoteState } = useModuleUpvote({
  lessonUuid: String(props.params.lessonUuid ?? ""),
  isMoeLesson: props.params.resourceDomain === "MOE",
});
const showUpvote = computed(
  () =>
    isDuplicated.value &&
    (props.params.resourceDomain === "MOE" || props.params.resourceDomain === "COMMUNITY") &&
    !isAdmin.value,
);

// Methods
const actionShown = function () {
  visible.value = true;
};

const actionHidden = function () {
  visible.value = false;
  modalEventBus.$emit("modal-hidden", vm.$.vnode.key);
};

const actionHideRequest = function (event) {
  // to prevent close modal upon clicking anywhere outside the modal container
  if (event["cv:reason"] !== "external-click") {
    actionSecondary();
  }
};

const actionSecondary = function () {
  visible.value = false;
  clearDirty();
  props.params.actionSecondary();
};

const actionPrimary = function () {
  visible.value = false;
  clearDirty();
  props.params.actionPrimary();
};

// Created
visible.value = true;

onMounted(async () => {
  if (showUpvote.value) {
    await fetchUpvoteState();
  }
});
</script>
